import type { ClientEvent } from '@shared/types';

/**
 * Client event tracker (§10): events are batched and flushed every 5 s, on visibility change and on page hide
 * (sendBeacon), so dwell times survive tab closes.
 */
const queue: ClientEvent[] = [];
let timer: number | null = null;
let installed = false;

function flush(useBeacon = false): void {
  if (queue.length === 0) return;
  const batch = queue.splice(0, 100);
  const body = JSON.stringify({ events: batch });
  if (useBeacon && navigator.sendBeacon) {
    navigator.sendBeacon('/api/events', new Blob([body], { type: 'application/json' }));
    return;
  }
  fetch('/api/events', { method: 'POST', headers: { 'content-type': 'application/json' }, body, credentials: 'same-origin', keepalive: true }).catch(() => {
    queue.unshift(...batch);
  });
}

export function track(event: ClientEvent['event'], data: Omit<ClientEvent, 'event' | 'ts'> = {}): void {
  queue.push({ event, ts: new Date().toISOString(), ...data });
  if (!installed) install();
  if (timer === null) timer = window.setTimeout(() => { timer = null; flush(); }, 5000);
}

export function flushNow(): void {
  if (timer !== null) { window.clearTimeout(timer); timer = null; }
  flush(true);
}

function install(): void {
  installed = true;
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flushNow(); });
  window.addEventListener('pagehide', () => flushNow());
}

/** Focus signals for a room: focus lost/gained (always logged) and hidden-content when blur mode is on. */
export function installFocusSignals(roomId: string, itemId: string | undefined, blur: boolean, onBlurChange: (hidden: boolean) => void): () => void {
  const onHide = () => {
    track('window.focus_lost', { room_id: roomId, item_id: itemId });
    if (blur) { onBlurChange(true); track('window.hidden_content', { room_id: roomId, item_id: itemId }); }
  };
  const onShow = () => { track('window.focus_gained', { room_id: roomId, item_id: itemId }); if (blur) onBlurChange(false); };
  const vis = () => (document.visibilityState === 'hidden' ? onHide() : onShow());
  window.addEventListener('blur', onHide);
  window.addEventListener('focus', onShow);
  document.addEventListener('visibilitychange', vis);
  return () => { window.removeEventListener('blur', onHide); window.removeEventListener('focus', onShow); document.removeEventListener('visibilitychange', vis); };
}
